import {useState} from 'react';

import {useParentLookup} from './useParentLookup';
import {
  ActiveEntitlement,
  InactiveEntitlement,
  LookupError,
  WebPurchases,
} from '~/components';
import {adminApi} from '~/store/rtkQuery';
import {formatDate} from '~/modules/utils';

export const ParentLookup = () => {
  const {parentLookup, search} = useParentLookup();
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const prefetchWebPurchases = adminApi.usePrefetch('webPurchasesLookup');

  const isSearching = parentLookup.status === 'searching';

  return (
    <div className="p-5">
      <h1 className="text-2xl font-semibold">Parent Lookup</h1>
      <p className="mt-2 text-sm text-gray-600">
        Look up a parent by email or userId to see their full profile,
        entitlements, and teens.
      </p>

      <div className="mt-4 form-control max-w-md">
        <label className="label" htmlFor="parent-query">
          <span className="label-text font-medium">
            Parent's email or userId
          </span>
        </label>
        <input
          id="parent-query"
          className="input input-bordered w-full"
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <button
          className={`btn mt-2 ${
            query.trim() && !isSearching ? 'btn-primary' : 'btn-outline'
          }`}
          onClick={() => {
            setSubmittedQuery(query.trim());
            prefetchWebPurchases({query: query.trim()});
            search(query);
          }}
          disabled={!query.trim() || isSearching}>
          {isSearching ? 'Searching...' : 'Search'}
        </button>
      </div>

      {parentLookup.status === 'error' && (
        <LookupError message={parentLookup.message} query={submittedQuery} />
      )}

      {parentLookup.status === 'error' && submittedQuery && (
        <div className="mt-4">
          <WebPurchases query={submittedQuery} />
        </div>
      )}

      {parentLookup.status === 'found' && (
        <ParentDetailView
          parentUserId={parentLookup.parentUserId}
          parentInfo={parentLookup.parentInfo}
          webPurchasesQuery={submittedQuery}
        />
      )}
    </div>
  );
};

const ParentDetailView = ({
  parentUserId,
  parentInfo,
  webPurchasesQuery,
}: {
  parentUserId: string;
  webPurchasesQuery: string;
  parentInfo: Extract<
    ReturnType<typeof useParentLookup>['parentLookup'],
    {status: 'found'}
  >['parentInfo'];
}) => {
  const name = [
    parentInfo.parentProfile.firstName,
    parentInfo.parentProfile.lastName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="mt-6">
      <div className="flex flex-col gap-3">
        <div className="card outline outline-zinc-200 outline-1">
          <div className="card-body">
            <div className="flex items-center gap-2">
              <h2 className="card-title">Profile</h2>
              <span
                className={`badge badge-sm ${
                  parentInfo.isParent ? 'badge-info' : 'badge-warning'
                }`}>
                {parentInfo.isParent ? 'Parent' : 'Teen'}
              </span>
            </div>
            <p className="font-medium">{name || parentInfo.email}</p>
            <p className="text-sm text-gray-600">
              <span className="font-bold">userId:</span> {parentUserId}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-bold">email:</span> {parentInfo.email}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-bold">Email verified:</span>{' '}
              {parentInfo.emailVerified ? 'Yes' : 'No'}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-bold">Created:</span>{' '}
              {formatDate(parentInfo.created)}
            </p>
          </div>
        </div>

        <div className="card outline outline-zinc-200 outline-1">
          <div className="card-body">
            <h2 className="card-title">Entitlements</h2>
            {parentInfo.entitlementsDetail.length === 0 ? (
              <p className="text-sm text-gray-600">No active entitlements.</p>
            ) : (
              <div className="space-y-3">
                {parentInfo.entitlementsDetail.map(entitlement => (
                  <ActiveEntitlement
                    key={entitlement.entitlement}
                    entitlement={entitlement}
                  />
                ))}
              </div>
            )}
            {parentInfo.lastInactiveEntitlement && (
              <InactiveEntitlement
                entitlement={parentInfo.lastInactiveEntitlement}
              />
            )}
          </div>
        </div>

        <WebPurchases query={webPurchasesQuery} hideWhenEmpty />

        <div className="card outline outline-zinc-200 outline-1">
          <div className="card-body">
            <h2 className="card-title">Teens</h2>
            {parentInfo.teens.length === 0 ? (
              <p className="text-sm text-gray-600">
                This parent has no teens on their account.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-xs">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Date of birth</th>
                      <th>Invite code</th>
                      <th>Claimed by</th>
                      <th>Consent id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parentInfo.teens.map(teen => (
                      <tr key={teen.id}>
                        <td>
                          {teen.teenFirstName} {teen.teenLastName}
                        </td>
                        <td>{teen.status ?? '-'}</td>
                        <td>{teen.dateOfBirth ?? '-'}</td>
                        <td>{teen.inviteCode ?? '-'}</td>
                        <td>{teen.claimedByUserId ?? '-'}</td>
                        <td>{teen.consentId ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
